import { Zap, Sparkles } from "lucide-react";
import { PixelatedLoader } from "@/components/ui/feedback";
import { GenerationProgress } from "./generation-progress";

interface GenerationLoadingStateProps {
  type?: "image" | "video";
  mediaType?: "IMAGE" | "VIDEO";
  progress: number;
  timeRemaining: number | null;
  prompt?: string;
  /** Whether to show the upgrade banner (typically for free/starter users) */
  showUpgradeBanner?: boolean;
  /** Callback when upgrade banner is clicked */
  onUpgradeClick?: () => void;
}

export function GenerationLoadingState({
  type,
  mediaType,
  progress,
  timeRemaining,
  prompt,
  showUpgradeBanner = false,
  onUpgradeClick,
}: GenerationLoadingStateProps) {
  const assetType =
    type === "image" || mediaType === "IMAGE" ? "Image" : "Video";

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        {/* Animated Pixelated Logo */}
        <div className="mb-8">
          <PixelatedLoader size="lg" />
        </div>

        {/* Title */}
        <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">
          Creating Your {assetType}
        </h1>
        <p className="text-[#888] mb-6">This usually takes a few minutes</p>

        {/* Upgrade Banner - promotion for free/starter users */}
        {showUpgradeBanner && onUpgradeClick && (
          <button
            onClick={onUpgradeClick}
            className="w-full mb-6 py-4 px-5 rounded-xl bg-gradient-to-r from-violet-600/25 via-amber-500/25 to-violet-600/25 border-2 border-violet-500/50 hover:border-violet-400 hover:from-violet-600/40 hover:via-amber-500/40 hover:to-violet-600/40 shadow-xl shadow-violet-500/20 hover:shadow-violet-500/30 transition-all group"
          >
            <div className="flex items-center justify-center gap-2.5 flex-wrap">
              <Zap className="size-5 text-amber-400" />
              <span className="text-sm font-bold text-white">
                Want Faster and 3x Better Generations?
              </span>
              <span className="text-sm font-bold text-violet-400 group-hover:text-violet-300 transition-colors underline underline-offset-2 decoration-2">
                Upgrade
              </span>
              <Sparkles className="size-5 text-violet-400" />
            </div>
          </button>
        )}

        {/* Progress Bar & Timer */}
        <GenerationProgress progress={progress} timeRemaining={timeRemaining} />

        {/* Prompt Preview */}
        {prompt && (
          <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.08] text-left">
            <p className="text-xs text-[#666] mb-1">Your prompt</p>
            <p className="text-sm text-[#999] line-clamp-2">{prompt}</p>
          </div>
        )}

        {/* Tips */}
        <p className="text-xs text-[#555] mt-8">
          You can leave this page - we'll save your generation to your assets.
        </p>
      </div>
    </div>
  );
}
